/**
 * Composer (DESIGN.md T11): ONE component for every host. Three layouts, same form:
 * - pane (default, user request 2026-10-09, like OWA): fills the reading pane of the three-pane
 *   mail view; folder tree and message list stay visible. The host renders it in place of the
 *   reading pane (it is a normal block element there);
 * - full width ("pop out"): covers the whole content area (render it inside the AppShell content);
 * - docked: card bottom-right, reading stays possible.
 * The user's last choice is remembered on this device. Phones always get the full screen.
 * `layout`/`onLayoutChange` make the layout controlled (the webmail decides where to render it).
 *
 * Header fields: From (when the user may send as other identities), To/Cc/Bcc as RecipientInput
 * chips with autocomplete, "To…/Cc…/Bcc…" open the address book, Bcc is toggled. Before sending,
 * typed names are checked (like Outlook); chips that are still unresolved block the send.
 *
 * Body: the shared RichTextEditor (rich text or plain text); replies/forwards open with the quoted
 * history in it (mail/quote.ts). Attachments: chips for files (with upload progress) and attached
 * messages; "Attach file" (Ctrl+Shift+A), drop files onto the composer, paste or drop non-picture
 * files into the text. Pictures dropped into the text stay inline. Ctrl+Enter sends (Outlook keymap).
 * While focus is inside the composer, other Ctrl shortcuts of the page (reply, mark unread, …) are
 * not triggered, so editor keys like Ctrl+U or Ctrl+K act on the text (or the field) only.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleAlertIcon, MailIcon, Maximize2Icon, PaperclipIcon, PanelRightIcon, PictureInPicture2Icon, SaveIcon, SendIcon, Trash2Icon, XIcon, type LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { useForm, type FieldPath } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../components/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/form';
import { Input } from '../components/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/tooltip';
import { LazyRichTextEditor } from '../editor/lazy-rich-text-editor';
import type { RichTextEditorHandle, RichTextValue } from '../editor/rich-text-editor';
import { getShortcuts, matchesChord, shortcutLabel, useShortcut } from '../hooks/use-shortcut';
import { cn } from '../lib/utils';
import { AddressBookDialog, type AddressBookLabels, type AddressBookSource } from './address-book-dialog';
import { attachmentIcon } from './attachment-icon';
import { FromSelect, type SenderIdentity } from './from-select';
import { checkNames, RecipientInput, type RecipientDirectory, type RecipientInputLabels } from './recipient-input';
import { needsAttention, recipientFromEntry, recipientLabel, recipientsFromInit, type PersonEntry, type Recipient, type RecipientInit } from './recipients';
import { MAIL_ITEM_DRAG_TYPE, readDraggedMailItem, type DraggedMailItem } from './mail-panes';
import { SignaturePicker, type ComposerSignatureOption, type SignaturePickerLabels } from './signature-picker';

export interface ComposerValues {
  /** Selected sender identity (undefined = the user's own address). */
  from?: string | undefined;
  to: Recipient[];
  cc: Recipient[];
  bcc: Recipient[];
  subject: string;
  body: RichTextValue;
}

export interface ComposerInitialValues {
  from?: string | undefined;
  to?: readonly RecipientInit[];
  cc?: readonly RecipientInit[];
  bcc?: readonly RecipientInit[];
  subject?: string;
  body?: RichTextValue;
}

export interface ComposerLabels {
  title: string;
  from: string;
  to: string;
  cc: string;
  bcc: string;
  /** Toggle button that shows the Bcc field. */
  showBcc: string;
  /** Accessible name of the address book buttons, contains `{field}`. */
  openAddressBook: string;
  subject: string;
  body: string;
  send: string;
  sending: string;
  discard: string;
  close: string;
  /** Layout buttons: reading pane, full width ("pop out"), docked window. */
  pane: string;
  fullWidth: string;
  docked: string;
  /** Placeholder of the empty To field. */
  recipientsHint: string;
  recipientsRequired: string;
  /** Contains `{addresses}`: the chips that still need attention. */
  invalidRecipients: string;
  recipient: RecipientInputLabels;
  addressBook: AddressBookLabels;
}

export type ComposerField = 'from' | 'to' | 'cc' | 'bcc' | 'subject';
export type ComposerLayout = 'pane' | 'full' | 'docked';
type RecipientField = 'to' | 'cc' | 'bcc';

/** One chip of the attachment list. */
export interface ComposerAttachment {
  id: string;
  name: string;
  /** file = file attachment (upload or the original's), message = attached mail (.eml). */
  kind: 'file' | 'message';
  /** Already formatted, e.g. "1.2 MB". */
  sizeLabel?: string | undefined;
  /** 0..1 while uploading; undefined when done. */
  progress?: number | undefined;
  /** Why the file could not be added (chip shown in error state until removed). */
  error?: string | undefined;
}

export interface ComposerAttachmentLabels {
  list: string;
  /** Contains `{name}`. */
  remove: string;
  /** Shown while mails are dragged over the composer. */
  drop: string;
  /** Shown while files are dragged over the composer. */
  dropFiles: string;
  attachFile: string;
  /** Contains `{name}`: accessible name of an upload's progress bar. */
  uploading: string;
}

export interface ComposerProps {
  labels: ComposerLabels;
  initialValues?: ComposerInitialValues;
  /** Fields shown but not editable, e.g. recipients and subject of a reply (set by the server). */
  readOnlyFields?: readonly ComposerField[];
  /** Info line above the body. */
  note?: string;
  onSend: (values: ComposerValues) => Promise<void>;
  onDiscard: () => void;
  /** Editor handle (focus, signature hook). */
  editorRef?: Ref<RichTextEditorHandle>;
  /** Autocomplete, check names and photos; without it, fields take typed addresses only. */
  directory?: RecipientDirectory;
  /** Address book behind the To…/Cc…/Bcc… buttons (hidden without it). */
  addressBook?: AddressBookSource;
  /** Identities the user may send as; the From field shows when there is more than one. */
  identities?: readonly SenderIdentity[];
  /** Controlled layout (default: the remembered choice). */
  layout?: ComposerLayout;
  onLayoutChange?: (layout: ComposerLayout) => void;
  /** Layouts offered by the layout buttons (default all three). */
  layouts?: readonly ComposerLayout[];
  /** Attachment chips: files (uploads, the original's on forward) and attached messages. */
  attachments?: readonly ComposerAttachment[];
  onRemoveAttachment?: (id: string) => void;
  /** Adds files (button, Ctrl+Shift+A, drop, paste); enables the attach controls. */
  onAddFiles?: (files: File[]) => void;
  /** Accept mail list rows dropped onto the composer (MAIL_ITEM_DRAG_TYPE). */
  onDropMailItem?: (item: DraggedMailItem) => void;
  /** Required when attachments, files or dropped mails are used. */
  attachmentLabels?: ComposerAttachmentLabels;
  /** Called when the form becomes dirty/clean (the host asks before throwing typed text away). */
  onDirtyChange?: (dirty: boolean) => void;
  /** Called on every change of a field (drafts: marks the composer as not saved). Read the values with `composerRef`. */
  onChange?: () => void;
  /** Current values for the host (draft saving), `from` normalized like on send. */
  composerRef?: Ref<ComposerHandle>;
  /** Close button (×) of the title bar; default: `onDiscard`. */
  onClose?: () => void;
  /** Drafts: "Save draft" in the footer and the save state next to it ("Saved 14:32"). */
  draft?: ComposerDraftControls;
  /** Signature picker in the footer (the host puts the default signature into `initialValues.body`, mail/signature.ts). */
  signature?: ComposerSignatureControls;
}

export interface ComposerSignatureControls {
  options: readonly ComposerSignatureOption[];
  /** Signature already in the initial body (undefined = none). */
  initialId?: string | undefined;
  /** A mandatory signature: it can be neither switched nor removed. */
  locked?: boolean | undefined;
  labels: SignaturePickerLabels;
}

/** Sets a React ref of either kind. */
function assignRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (typeof ref === 'function') ref(value);
  else if (ref) (ref as { current: T | null }).current = value;
}

export interface ComposerHandle {
  getValues: () => ComposerValues;
}

export interface ComposerDraftControls {
  onSave: () => void;
  saveLabel: string;
  /** Save state, e.g. "Saved 14:32", "Saving…", "Not saved" (announced politely to screen readers). */
  status?: string | undefined;
  /** The status is an error (shown in the destructive color). */
  statusError?: boolean | undefined;
  saving?: boolean | undefined;
}

const LAYOUT_KEY = 'platform.composer.layout';
const LAYOUTS: readonly ComposerLayout[] = ['pane', 'full', 'docked'];
const RECIPIENT_FIELDS: readonly RecipientField[] = ['to', 'cc', 'bcc'];
const LAYOUT_ICONS: Record<ComposerLayout, LucideIcon> = { pane: PanelRightIcon, full: Maximize2Icon, docked: PictureInPicture2Icon };

/** Directory without a server: no suggestions, names stay unresolved. */
const OFFLINE_DIRECTORY: RecipientDirectory = {
  suggest: () => Promise.resolve([]),
  resolve: (entries) => Promise.resolve(entries.map((input) => ({ input, status: 'unresolved' as const, match: null, candidates: [] }))),
};

/** Last layout the user picked (per device; storage may be unavailable). Default: the reading pane. */
export function readComposerLayout(): ComposerLayout {
  try {
    const stored = globalThis.localStorage?.getItem(LAYOUT_KEY);
    return LAYOUTS.find((layout) => layout === stored) ?? 'pane';
  } catch {
    return 'pane';
  }
}

export function writeComposerLayout(layout: ComposerLayout): void {
  try {
    globalThis.localStorage?.setItem(LAYOUT_KEY, layout);
  } catch {
    // Not remembered; still applied now.
  }
}

/** Composer chords that work while typing in it; every other Ctrl shortcut of the page is kept away. */
const COMPOSER_SHORTCUTS = ['mail.send', 'mail.attachFile'] as const;

function isolateShortcuts(event: KeyboardEvent<HTMLElement>): void {
  if (!(event.ctrlKey || event.metaKey)) return;
  if (COMPOSER_SHORTCUTS.some((action) => getShortcuts(action).some((chord) => matchesChord(event.nativeEvent, chord)))) return;
  event.stopPropagation();
}

/** Position classes per layout; below md every layout is the full screen. */
const LAYOUT_CLASSES: Record<ComposerLayout, string> = {
  pane: 'md:relative md:inset-auto md:z-auto md:h-full md:min-h-0 md:min-w-0 md:flex-1',
  full: 'md:absolute md:inset-0',
  docked: 'border shadow-lg md:inset-auto md:right-4 md:bottom-4 md:h-144 md:max-h-11/12 md:w-full md:max-w-2xl md:rounded-lg',
};

function AttachmentChips({ items, labels, onRemove }: { items: readonly ComposerAttachment[]; labels: ComposerAttachmentLabels; onRemove?: ((id: string) => void) | undefined }) {
  return (
    <ul aria-label={labels.list} className="flex shrink-0 flex-wrap gap-2">
      {items.map((item) => {
        const Icon = item.error ? CircleAlertIcon : attachmentIcon(item.name, item.kind);
        const uploading = item.progress !== undefined && !item.error;
        return (
          <li
            key={item.id}
            title={item.error}
            className={cn(
              'relative inline-flex h-control-sm max-w-full items-center gap-1.5 overflow-hidden rounded-md border pr-1 pl-2 text-sm',
              item.error && 'border-destructive text-destructive',
            )}
          >
            <Icon aria-hidden className={cn('size-4 shrink-0', !item.error && 'text-muted-foreground')} />
            <span className="max-w-60 truncate">{item.name}</span>
            {item.sizeLabel && <span className="shrink-0 text-xs text-muted-foreground">{item.sizeLabel}</span>}
            {item.error && <span className="sr-only">{item.error}</span>}
            {onRemove && (
              <Button type="button" variant="ghost" size="icon-sm" aria-label={labels.remove.replace('{name}', item.name)} onClick={() => onRemove(item.id)}>
                <XIcon />
              </Button>
            )}
            {uploading && (
              <span
                role="progressbar"
                aria-label={labels.uploading.replace('{name}', item.name)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round((item.progress ?? 0) * 100)}
                className="absolute inset-x-0 bottom-0 h-0.5 bg-muted"
              >
                <span className="block h-full bg-primary transition-[width] duration-150" style={{ width: `${Math.round((item.progress ?? 0) * 100)}%` }} />
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function Composer({
  labels,
  initialValues,
  readOnlyFields = [],
  note,
  onSend,
  onDiscard,
  editorRef,
  directory = OFFLINE_DIRECTORY,
  addressBook,
  identities = [],
  layout: controlledLayout,
  onLayoutChange,
  layouts = LAYOUTS,
  attachments = [],
  onRemoveAttachment,
  onAddFiles,
  onDropMailItem,
  attachmentLabels,
  onDirtyChange,
  onChange,
  composerRef,
  onClose = onDiscard,
  draft,
  signature,
}: ComposerProps) {
  const readOnly = (field: ComposerField) => readOnlyFields.includes(field);
  const editorHandle = useRef<RichTextEditorHandle | null>(null);
  const setEditorHandle = useCallback(
    (handle: RichTextEditorHandle | null) => {
      editorHandle.current = handle;
      assignRef(editorRef, handle);
    },
    [editorRef],
  );
  const [signatureId, setSignatureId] = useState(signature?.initialId);
  const pickSignature = (option: ComposerSignatureOption | undefined) => {
    if (option) editorHandle.current?.setSignature({ id: option.id, html: option.html });
    else editorHandle.current?.removeSignature();
    setSignatureId(option?.id);
  };
  const [ownLayout, setOwnLayout] = useState<ComposerLayout>(readComposerLayout);
  const layout = controlledLayout ?? ownLayout;
  const [showBcc, setShowBcc] = useState((initialValues?.bcc?.length ?? 0) > 0);
  const [picker, setPicker] = useState<RecipientField>();
  const [dropping, setDropping] = useState<'mail' | 'files'>();
  const fileInput = useRef<HTMLInputElement>(null);

  const dropKind = (event: DragEvent<HTMLElement>): 'mail' | 'files' | undefined => {
    const types = event.dataTransfer.types;
    if (onDropMailItem && types.includes(MAIL_ITEM_DRAG_TYPE)) return 'mail';
    // Over the text, the editor takes files itself (pictures inline, other files attached).
    const overText = event.target instanceof Element && event.target.closest('.rich-text-content') !== null;
    if (onAddFiles && types.includes('Files') && !overText) return 'files';
    return undefined;
  };
  const dropHandlers = {
    onDragOver: (event: DragEvent<HTMLElement>) => {
      const kind = dropKind(event);
      setDropping(kind);
      if (!kind) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropping(undefined);
    },
    // Files dropped into the text are handled by the editor (pictures inline, others attached).
    onDrop: (event: DragEvent<HTMLElement>) => {
      setDropping(undefined);
      const kind = dropKind(event);
      if (!kind) return;
      event.preventDefault();
      if (kind === 'files') {
        onAddFiles?.(Array.from(event.dataTransfer.files));
        return;
      }
      const item = readDraggedMailItem(event.dataTransfer);
      if (item) onDropMailItem?.(item);
    },
  };
  const changeLayout = (next: ComposerLayout) => {
    writeComposerLayout(next);
    setOwnLayout(next);
    onLayoutChange?.(next);
  };

  const schema = useMemo(() => {
    const recipients = z.array(z.custom<Recipient>()).superRefine((list, ctx) => {
      const open = list.filter(needsAttention);
      if (open.length > 0) ctx.addIssue({ code: 'custom', message: labels.invalidRecipients.replace('{addresses}', open.map(recipientLabel).join(', ')) });
    });
    return z
      .object({
        from: z.string().optional(),
        to: recipients,
        cc: recipients,
        bcc: recipients,
        subject: z.string(),
        body: z.object({ format: z.enum(['html', 'text']), content: z.string() }),
      })
      .superRefine((v, ctx) => {
        if (v.to.length + v.cc.length + v.bcc.length === 0) ctx.addIssue({ code: 'custom', path: ['to'], message: labels.recipientsRequired });
      });
  }, [labels]);

  const form = useForm<ComposerValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      from: initialValues?.from ?? identities[0]?.email,
      to: recipientsFromInit(initialValues?.to),
      cc: recipientsFromInit(initialValues?.cc),
      bcc: recipientsFromInit(initialValues?.bcc),
      subject: initialValues?.subject ?? '',
      body: initialValues?.body ?? { format: 'html', content: '' },
    },
  });

  const dirty = form.formState.isDirty;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Every field change (also the editor) reaches the host, e.g. to autosave a draft.
  const changed = useRef(onChange);
  useEffect(() => {
    changed.current = onChange;
  });
  useEffect(() => {
    const subscription = form.watch(() => changed.current?.());
    return () => subscription.unsubscribe();
  }, [form]);

  /** The user's own address is sent as "no From" (the server uses the login identity). */
  const normalized = (values: ComposerValues): ComposerValues => {
    const own = identities[0]?.email;
    return { ...values, from: values.from && values.from !== own ? values.from : undefined };
  };
  useImperativeHandle(composerRef, () => ({ getValues: () => normalized(form.getValues()) }));

  const submit = form.handleSubmit(async (values) => {
    await onSend(normalized(values));
  });

  /** Outlook behaviour: typed names are checked before sending. */
  const send = async () => {
    for (const field of RECIPIENT_FIELDS) {
      const list = form.getValues(field);
      if (list.some((r) => r.status === 'pending')) form.setValue(field, await checkNames(list, directory));
    }
    await submit();
  };

  useShortcut('mail.send', () => void send(), { allowInInputs: true });
  useShortcut('mail.attachFile', () => fileInput.current?.click(), { allowInInputs: true, enabled: onAddFiles !== undefined });
  const sending = form.formState.isSubmitting;
  const layoutLabels: Record<ComposerLayout, string> = { pane: labels.pane, full: labels.fullWidth, docked: labels.docked };

  const moveRecipient = (recipient: Recipient, fromField: string) => {
    const field = fromField as RecipientField;
    if (!RECIPIENT_FIELDS.includes(field)) return;
    form.setValue(
      field,
      form.getValues(field).filter((r) => r.id !== recipient.id),
      { shouldDirty: true },
    );
  };

  const applyAddressBook = (selection: Record<string, PersonEntry[]>) => {
    for (const field of RECIPIENT_FIELDS) {
      const added = selection[field] ?? [];
      if (added.length === 0) continue;
      const current = form.getValues(field);
      const known = new Set(current.map((r) => r.email.toLowerCase()));
      form.setValue(field, [...current, ...added.filter((e) => !known.has(e.email.toLowerCase())).map(recipientFromEntry)], { shouldDirty: true, shouldValidate: form.formState.isSubmitted });
      if (field === 'bcc') setShowBcc(true);
    }
  };

  const fieldLabel: Record<RecipientField, string> = { to: labels.to, cc: labels.cc, bcc: labels.bcc };
  const canPick = (field: RecipientField) => addressBook !== undefined && !readOnly(field);

  const recipientRow = (name: RecipientField & FieldPath<ComposerValues>, extra?: ReactNode) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <div className="flex items-start gap-2">
            {canPick(name) ? (
              <Button
                type="button"
                variant="outline"
                className="w-16 shrink-0"
                aria-label={labels.openAddressBook.replace('{field}', fieldLabel[name])}
                onClick={() => setPicker(name)}
              >
                {fieldLabel[name]}…
              </Button>
            ) : (
              <FormLabel className="flex h-control w-16 shrink-0 items-center">{fieldLabel[name]}</FormLabel>
            )}
            <FormControl>
              <RecipientInput
                field={name}
                label={fieldLabel[name]}
                value={field.value}
                onChange={(next) => field.onChange(next)}
                directory={directory}
                labels={labels.recipient}
                readOnly={readOnly(name)}
                autoFocus={name === 'to' && !readOnly('to')}
                invalid={fieldState.invalid}
                placeholder={name === 'to' ? labels.recipientsHint : undefined}
                onDropRecipient={moveRecipient}
              />
            </FormControl>
            {extra}
          </div>
          <FormMessage className="pl-18" />
        </FormItem>
      )}
    />
  );

  const fromIdentities = identities.length > 1 && !readOnly('from');
  const attachLabel = attachmentLabels ? `${attachmentLabels.attachFile} (${shortcutLabel('mail.attachFile')})` : '';

  return (
    <section
      aria-label={labels.title}
      data-layout={layout}
      onKeyDown={isolateShortcuts}
      {...dropHandlers}
      className={cn('mail-composer fixed inset-0 z-40 flex min-w-0 flex-col bg-card', LAYOUT_CLASSES[layout])}
    >
      <div className="flex h-bar shrink-0 items-center justify-between gap-2 border-b px-3">
        <h2 className="truncate text-sm font-semibold">{labels.title}</h2>
        <div className="flex items-center gap-1">
          {layouts
            .filter((option) => option !== layout)
            .map((option) => {
              const Icon = LAYOUT_ICONS[option];
              return (
                <Tooltip key={option}>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon-sm" aria-label={layoutLabels[option]} className="hidden md:inline-flex" onClick={() => changeLayout(option)}>
                      <Icon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{layoutLabels[option]}</TooltipContent>
                </Tooltip>
              );
            })}
          <Button variant="ghost" size="icon-sm" aria-label={labels.close} onClick={onClose}>
            <XIcon />
          </Button>
        </div>
      </div>
      <Form {...form}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
          className="flex min-h-0 min-w-0 w-full flex-1 flex-col gap-2 overflow-y-auto p-3"
          noValidate
        >
          {fromIdentities && (
            <FormField
              control={form.control}
              name="from"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel className="w-16 shrink-0">{labels.from}</FormLabel>
                    <FormControl>
                      <FromSelect identities={identities} value={field.value ?? identities[0]?.email ?? ''} onChange={field.onChange} label={labels.from} pickerLabels={labels.recipient} />
                    </FormControl>
                  </div>
                </FormItem>
              )}
            />
          )}
          {recipientRow(
            'to',
            !showBcc && !readOnly('bcc') ? (
              <Button type="button" variant="ghost" className="shrink-0" aria-pressed={false} onClick={() => setShowBcc(true)}>
                {labels.showBcc}
              </Button>
            ) : undefined,
          )}
          {recipientRow('cc')}
          {showBcc && recipientRow('bcc')}
          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel className="w-16 shrink-0">{labels.subject}</FormLabel>
                  <FormControl>
                    <Input className="flex-1" readOnly={readOnly('subject')} {...field} />
                  </FormControl>
                </div>
              </FormItem>
            )}
          />
          {note && <p className="text-xs text-muted-foreground">{note}</p>}
          {attachments.length > 0 && attachmentLabels && <AttachmentChips items={attachments} labels={attachmentLabels} onRemove={onRemoveAttachment} />}
          <FormField
            control={form.control}
            name="body"
            render={({ field }) => (
              <LazyRichTextEditor
                handleRef={setEditorHandle}
                label={labels.body}
                placeholder={labels.body}
                autoFocus={readOnly('to')}
                defaultValue={field.value}
                onChange={field.onChange}
                onFiles={onAddFiles}
                className="min-h-48 min-w-0 flex-1"
                compactToolbar
              />
            )}
          />
          <div className="flex min-w-0 shrink-0 flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-1">
              <Button type="button" variant="ghost" aria-label={labels.discard} onClick={onDiscard}>
                <Trash2Icon />
                <span className={cn('hidden sm:inline', layout === 'docked' && 'md:hidden')}>{labels.discard}</span>
              </Button>
              {onAddFiles && attachmentLabels && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="button" variant="ghost" aria-label={attachmentLabels.attachFile} onClick={() => fileInput.current?.click()}>
                      <PaperclipIcon />
                      <span className={cn('hidden sm:inline', layout === 'docked' && 'md:hidden')}>{attachmentLabels.attachFile}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{attachLabel}</TooltipContent>
                </Tooltip>
              )}
              {signature && (signature.options.length > 0 || signatureId !== undefined) && (
                <SignaturePicker options={signature.options} value={signatureId} onChange={pickSignature} locked={signature.locked} labels={signature.labels} />
              )}
              {draft && (
                <Button type="button" variant="ghost" disabled={draft.saving || sending} aria-label={draft.saveLabel} onClick={draft.onSave}>
                  <SaveIcon />
                  <span className={cn('hidden sm:inline', layout === 'docked' && 'md:hidden')}>{draft.saveLabel}</span>
                </Button>
              )}
            </div>
            <div className="flex min-w-0 items-center gap-2">
              {draft && (
                <span role="status" aria-live="polite" className={cn('truncate text-xs', draft.statusError ? 'text-destructive' : 'text-muted-foreground')}>
                  {draft.status}
                </span>
              )}
              <Button type="submit" disabled={sending}>
                <SendIcon />
                {sending ? labels.sending : labels.send}
              </Button>
            </div>
          </div>
        </form>
      </Form>
      {onAddFiles && (
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          aria-hidden
          data-attach-input=""
          tabIndex={-1}
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = '';
            if (files.length > 0) onAddFiles(files);
          }}
        />
      )}
      {addressBook && (
        <AddressBookDialog
          open={picker !== undefined}
          onOpenChange={(open) => {
            if (!open) setPicker(undefined);
          }}
          source={addressBook}
          fields={RECIPIENT_FIELDS.filter((f) => !readOnly(f)).map((f) => ({ id: f, label: fieldLabel[f] }))}
          initialField={picker ?? 'to'}
          onApply={applyAddressBook}
          labels={labels.addressBook}
        />
      )}
      {dropping && attachmentLabels && (
        <div aria-hidden className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-primary bg-background/90 text-sm font-medium text-primary">
          {dropping === 'mail' ? <MailIcon className="size-5" /> : <PaperclipIcon className="size-5" />}
          {dropping === 'mail' ? attachmentLabels.drop : attachmentLabels.dropFiles}
        </div>
      )}
    </section>
  );
}
